import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getBanyanproEmbedEnabled, isEmbedPostMessageMode } from '../config/embedConfig';
import { LMS_EMBED_DISABLED } from '../errors/embedErrors';
import { getEmbedErrorPresentation } from '../errors/embedErrors';
import { startPostMessageLaunch } from '../launch/LaunchController';
import { runLmsLaunchFlow } from '../launch/launchFlow';
import { stripAssertionFromUrl } from '../security/stripAssertionFromUrl';
import { extractAssertionJwt } from '../../../utils/lmsLaunch';
import { EmbedShell } from './EmbedShell';
import type { LmsLaunchErrorPresentation } from '../../../utils/lmsLaunchErrors';

type EmbedPhase = 'waiting' | 'launching' | 'error';

/**
 * Minimal iframe shell for BanyanPro embed (/embed/banyanpro).
 * postMessage mode when configured; optional query fallback in auto mode.
 */
export const BanyanProEmbedPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeLmsSession } = useAuth();
  const [phase, setPhase] = useState<EmbedPhase>('waiting');
  const [errorPresentation, setErrorPresentation] = useState<LmsLaunchErrorPresentation>(
    getEmbedErrorPresentation(LMS_EMBED_DISABLED)
  );
  const startedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | undefined>();

  const returnUrlParam = searchParams.get('returnUrl') ?? searchParams.get('return_url');

  const runQueryFallback = useCallback(async () => {
    const assertion = extractAssertionJwt(searchParams);
    if (!assertion) {
      return false;
    }
    stripAssertionFromUrl();
    setPhase('launching');
    const result = await runLmsLaunchFlow(assertion, {
      returnUrl: returnUrlParam,
      completeLmsSession,
    });
    if (result.ok) {
      navigate(result.targetPath, { replace: true });
      return true;
    }
    setErrorPresentation(result.presentation);
    setPhase('error');
    return true;
  }, [searchParams, returnUrlParam, completeLmsSession, navigate]);

  const startEmbed = useCallback(() => {
    cleanupRef.current?.();
    setPhase('waiting');
    setErrorPresentation(getEmbedErrorPresentation(LMS_EMBED_DISABLED));

    if (!getBanyanproEmbedEnabled()) {
      setErrorPresentation(getEmbedErrorPresentation(LMS_EMBED_DISABLED));
      setPhase('error');
      return;
    }

    if (isEmbedPostMessageMode()) {
      cleanupRef.current = startPostMessageLaunch(
        { completeLmsSession, navigate, returnUrl: returnUrlParam },
        {
          onWaiting: () => setPhase('waiting'),
          onLaunching: () => setPhase('launching'),
          onError: (p) => {
            setErrorPresentation(p);
            setPhase('error');
          },
          onSuccess: () => {
            /* navigate handled in controller */
          },
        }
      );
      return;
    }

    void runQueryFallback();
  }, [completeLmsSession, navigate, returnUrlParam, runQueryFallback]);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    void (async () => {
      // Query-param launch works on embed URL regardless of postMessage mode (backward compatible).
      if (await runQueryFallback()) {
        return;
      }
      if (!getBanyanproEmbedEnabled()) {
        setErrorPresentation(getEmbedErrorPresentation(LMS_EMBED_DISABLED));
        setPhase('error');
        return;
      }
      if (isEmbedPostMessageMode()) {
        startEmbed();
        return;
      }
      setErrorPresentation(getEmbedErrorPresentation(LMS_EMBED_DISABLED));
      setPhase('error');
    })();
    return () => {
      cleanupRef.current?.();
    };
  }, [runQueryFallback, startEmbed]);

  const handleRetry = () => {
    startedRef.current = true;
    startEmbed();
  };

  return (
    <EmbedShell phase={phase} errorPresentation={errorPresentation} onRetry={handleRetry} />
  );
};
